// React-Query hooks + shared types for the collaboration layer (chat, nudges, people, co-op).
// Everything is polled — no sockets — so intervals are chosen to feel live without hammering the API.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ChatAttachment, ChatReaction, ChatReactionEmoji, CoopAnchor } from '@technohull/shared';
import { api } from '../lib/api.js';

export type { ChatAttachment, ChatReaction };

export interface CollabPerson { uid: string; name: string; avatar: string | null }
export interface PresenceViewer { uid: string; name: string; department: string; avatar: string | null }
export interface CollabThread {
  id: string;
  title: string | null;
  isGroup: boolean;
  members: CollabPerson[];
  lastMessage: { fromName: string; body: string; at: number } | null;
  unread: number;
}
export interface CollabMessage {
  id: number;
  fromUid: string;
  fromName: string;
  /** the plain text ('' once deleted) */
  body: string;
  /** the formatted version, cleaned by the server; null for an old plain-text message or a deleted one */
  html: string | null;
  at: number;
  editedAt: number | null;
  deleted: boolean;
  mine: boolean;
  attachments: ChatAttachment[];
  reactions: ChatReaction[];
}
/** someone else in my co-op room: their page, and their pointer as an element spot (anchor) with the
 *  viewport fraction (x, y) as the fallback. */
export interface CoopPeer { uid: string; name: string; x: number; y: number; anchor: CoopAnchor | null; color: string; path: string }
/** a poke, or a co-op invite (with the inviter's room). */
export interface CollabNudge { fromUid: string; fromName: string; kind: 'poke' | 'coop'; path: string; room?: string | null; at: number }

/** everyone but me, for the "new message" / group picker. */
/** a co-op room refreshes its pages when anyone saves something; chatting, pokes and marking things read
 *  change nothing on those pages, so these mutations say so (see CollabContext's save listener). */
export const COOP_QUIET = { coopQuiet: true } as const;

export function usePeople(enabled: boolean) {
  return useQuery({
    queryKey: ['collab', 'people'],
    queryFn: () => api.get<{ people: CollabPerson[] }>('/api/collab/people'),
    enabled,
    staleTime: 60_000,
  });
}

/** my conversations, with unread counts. Polls while the chat panel is open. */
export function useThreads(enabled: boolean) {
  return useQuery({
    queryKey: ['collab', 'threads'],
    queryFn: () => api.get<{ threads: CollabThread[] }>('/api/collab/threads'),
    enabled,
    refetchInterval: enabled ? 15_000 : false,
  });
}

/** messages in one thread. Polls quickly while that thread is open. */
export function useThreadMessages(threadId: string | null) {
  return useQuery({
    queryKey: ['collab', 'messages', threadId],
    queryFn: () => api.get<{ messages: CollabMessage[] }>(`/api/collab/messages?thread=${encodeURIComponent(threadId!)}`),
    enabled: !!threadId,
    refetchInterval: threadId ? 5_000 : false,
  });
}

const messagesKey = (thread: string) => ['collab', 'messages', thread] as const;
const refreshThread = (qc: QueryClient, thread: string) => {
  void qc.invalidateQueries({ queryKey: messagesKey(thread) });
  void qc.invalidateQueries({ queryKey: ['collab', 'threads'] });
};
/** change one message in the cached thread straight away (the next poll brings the server's view). */
const patchMessage = (qc: QueryClient, thread: string, id: number, fn: (m: CollabMessage) => CollabMessage) =>
  qc.setQueryData<{ messages: CollabMessage[] }>(messagesKey(thread), (prev) =>
    prev ? { messages: prev.messages.map((m) => (m.id === id ? fn(m) : m)) } : prev);

/** send a message: its formatted text (the server cleans it and keeps a plain-text copy) and the ids of
 *  files already uploaded to the thread. */
export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { thread: string; html: string; attachments: string[] }) => api.post<{ message: CollabMessage }>('/api/collab/message', v),
    onSuccess: (r, v) => {
      // show it at once rather than on the refetch
      qc.setQueryData<{ messages: CollabMessage[] }>(messagesKey(v.thread), (prev) =>
        prev && r?.message && !prev.messages.some((m) => m.id === r.message.id) ? { messages: [...prev.messages, r.message] } : prev);
      refreshThread(qc, v.thread);
    },
  });
}

export function useEditMessage() {
  const qc = useQueryClient();
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { thread: string; id: number; html: string }) =>
      api.post<{ ok: true; editedAt: number; html: string | null; body: string }>('/api/collab/message/edit', { id: v.id, html: v.html }),
    onSuccess: (r, v) => {
      patchMessage(qc, v.thread, v.id, (m) => ({ ...m, html: r.html, body: r.body, editedAt: r.editedAt }));
      refreshThread(qc, v.thread);
    },
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { thread: string; id: number }) => api.post('/api/collab/message/delete', { id: v.id }),
    onSuccess: (_r, v) => {
      patchMessage(qc, v.thread, v.id, (m) => ({ ...m, deleted: true, body: '', html: null, attachments: [], reactions: [] }));
      refreshThread(qc, v.thread);
    },
  });
}

/** add (on) or take back my reaction; the chip changes at once and goes back if the server says no. */
export function useReact() {
  const qc = useQueryClient();
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { thread: string; id: number; emoji: ChatReactionEmoji; on: boolean }) =>
      api.post('/api/collab/react', { id: v.id, emoji: v.emoji, on: v.on }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: messagesKey(v.thread) });
      const prev = qc.getQueryData<{ messages: CollabMessage[] }>(messagesKey(v.thread));
      patchMessage(qc, v.thread, v.id, (m) => ({ ...m, reactions: toggleReaction(m.reactions, v.emoji, v.on) }));
      return { prev };
    },
    onError: (_e, v, c) => { if (c?.prev) qc.setQueryData(messagesKey(v.thread), c.prev); },
    onSettled: (_r, _e, v) => void qc.invalidateQueries({ queryKey: messagesKey(v.thread) }),
  });
}

/** my reaction added to (or taken from) a message's chips, as the server will count it. */
export function toggleReaction(list: readonly ChatReaction[], emoji: string, on: boolean): ChatReaction[] {
  const has = list.find((r) => r.emoji === emoji);
  if (on) {
    if (has?.mine) return [...list];
    if (has) return list.map((r) => (r === has ? { ...r, count: r.count + 1, mine: true, names: ['You', ...r.names] } : r));
    return [...list, { emoji, count: 1, mine: true, names: ['You'] }];
  }
  if (!has?.mine) return [...list];
  return list
    .map((r) => (r === has ? { ...r, count: r.count - 1, mine: false, names: r.names.filter((n) => n !== 'You') } : r))
    .filter((r) => r.count > 0);
}

/** the address of a file sent in chat (`download` asks for it as a download rather than to open). */
export const chatFileUrl = (id: string, download = false) => `/api/collab/file?id=${encodeURIComponent(id)}${download ? '&dl=1' : ''}`;

/** put one file in a thread (unsent until its message goes), reporting progress. */
export function uploadChatFile(
  thread: string,
  file: { blob: Blob; name: string; type: string; width: number | null; height: number | null },
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
) {
  const q = new URLSearchParams({ thread, name: file.name });
  if (file.width && file.height) { q.set('w', String(file.width)); q.set('h', String(file.height)); }
  return api.uploadWithProgress<{ attachment: ChatAttachment }>(`/api/collab/upload?${q}`, file.blob, file.type || 'application/octet-stream', opts);
}

/** take an uploaded file back out before sending (best effort: what's left is swept after a day). */
export const discardChatUpload = (id: string) => api.post('/api/collab/upload/discard', { id }).catch(() => undefined);
/** the same for all the files a closing page had waiting (sent as the page goes). */
export const discardChatUploadsOnLeave = (ids: string[]) => api.beacon('/api/collab/upload/discard', { ids });

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { memberUids: string[]; title?: string }) => api.post<{ id: string }>('/api/collab/thread', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collab', 'threads'] }),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { thread: string; lastId: number }) => api.post('/api/collab/read', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collab', 'threads'] }),
  });
}

export function useNudge() {
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { toUid: string; kind?: 'poke' | 'coop'; path?: string; room?: string }) => api.post('/api/collab/nudge', v),
  });
}
